/**
 * `DELETE /gateway/api/assist/rovo/v1/pins/:pinId`. Mirrors the `rovo-spaces` fetch-file
 * convention (see `useDeleteSpace.fetch.ts`): `credentials: 'include'` so the existing
 * authenticated session cookie is sent, and raw `fetch` rather than `performHttpFetch` —
 * `performHttpFetch` unconditionally calls `response.json()`, which throws on the bodyless
 * response a DELETE typically returns.
 */
import { FetchError } from '@atlassian/conversation-assistant-utils/httpUtils';

import type { PinsFetchContext } from './listPinsFetch';

const PINS_API_PATH = '/gateway/api/assist/rovo/v1/pins';

// Fixed per rovo-for-you/for-you-panel's own convention for this surface — see e.g.
// `useInsightsStatus`'s `experienceId: 'ai-mate'` call in for-you-panel/index.tsx.
const EXPERIENCE_ID = 'ai-mate';

const getDeletePinPath = (pinId: string) => `${PINS_API_PATH}/${pinId}`;

export type DeletePinFetchContext = PinsFetchContext & {
	pinId: string;
};

export const deletePinFetch = async ({
	pinId,
	cloudId,
	product,
}: DeletePinFetchContext): Promise<void> => {
	const path = getDeletePinPath(pinId);
	let response: Response;
	try {
		response = await fetch(path, {
			method: 'DELETE',
			headers: {
				'Content-Type': 'application/json',
				'x-cloudid': cloudId,
				'x-product': product ?? '',
				'x-experience-id': EXPERIENCE_ID,
			},
			credentials: 'include',
		});
	} catch (err) {
		const originalErrorMessage = err instanceof Error ? err.message : 'Unknown error';
		throw new FetchError(
			`deletePinFetch error while fetching ${path}. Original error: ${originalErrorMessage}`,
			{ errorCode: 'deletePinFetchError' },
		);
	}
	if (!response.ok) {
		const body = await response.text().catch(() => '');
		throw new FetchError(
			`deletePinFetch error while fetching ${path}. Original error: [Code ${response.status}] ${body}`,
			{ errorCode: 'deletePinFetchError', statusCode: response.status },
		);
	}
};
